import type { CategoryModel, ProductGroupModel } from '@/shared/generated'
import type { Forest, ForestInput } from '@/shared/lib/forest'
import type { ProductGroupCatalog } from './product-group-tree'

import { createForest } from '@/shared/lib/forest'

import { createCategoryCatalog } from './catalog'
import { nodeId } from './node-id'
import { createProductGroupCatalog } from './product-group-tree'

export type PickerNodeData =
	| { kind: 'pg'; name: string; pgId: string }
	| { kind: 'category'; name: string; pgId: string; categoryId: string }

export type PickerForest = Forest<PickerNodeData>

export type ProductGroupsData = {
	catalog: ProductGroupCatalog
	pickerForest: PickerForest
}

/**
 * One composite forest: PG nodes at depth 0, each PG's projection beneath it
 * with namespaced ids. Order is PG name, then catalog order. No sort is passed
 * because insertion order already matches.
 */
export const createProductGroupPickerForest = (
	pgCatalog: ProductGroupCatalog
): PickerForest => {
	const items: ForestInput<PickerNodeData>[] = []
	for (const group of pgCatalog.groups.values()) {
		const pgNode = nodeId.pg(group.id)
		items.push({
			id: pgNode,
			parentId: null,
			data: { kind: 'pg', name: group.name, pgId: group.id },
		})
		for (const categoryId of group.forest.ids) {
			const parent = group.forest.parentOf.get(categoryId) ?? null
			items.push({
				id: nodeId.cat(group.id, categoryId),
				parentId: parent === null ? pgNode : nodeId.cat(group.id, parent),
				data: {
					kind: 'category',
					name: group.forest.dataOf.get(categoryId)!.name,
					pgId: group.id,
					categoryId,
				},
			})
		}
	}

	return createForest(items)
}

/** Convenience for non-hook callers (query functions, tests). */
export const buildPickerForest = (
	categories: CategoryModel[],
	productGroups: ProductGroupModel[]
): ProductGroupsData => {
	const catalog = createProductGroupCatalog(
		createCategoryCatalog(categories),
		productGroups
	)

	return { catalog, pickerForest: createProductGroupPickerForest(catalog) }
}
